import { inkedClouds, skyDome, thunderhead } from "../sky.ts";
import { paintPanels } from "./panels.ts";
import { type DressContext, type MapLook, onStage, type Owned, type StageLook } from "../types.ts";

/** Where each stage's point stands, in game x, z (tools/map/orlando.py, STAGES). */
export const CENTERS: [number, number][] = [
  [-175, 0],
  [0, 0],
  [175, 0],
];

/**
 * Each stage's own sky and light. Lake Eola: a clear, humid afternoon, the sun in the
 * west-south-west on the band shell and the fountain. International Drive: golden hour, the sun
 * low in the west, warm light and a peach sky. The Orange Blossom Trail: a hot, hazy afternoon
 * under a high sun.
 */
const STAGES: Required<Pick<StageLook, "sky" | "fog" | "hemi" | "sun" | "glow">>[] = [
  {
    sky: { top: "#72b6dc", horizon: "#e6f2ec" },
    fog: { color: "#dcebe5", density: 0.0028 },
    hemi: { intensity: 0.84, sky: "#dde9f3", ground: "#a7a291", dir: [0.1, 1, 0.05] },
    sun: { dir: [0.76, -0.588, -0.275], intensity: 0.95, color: "#fff2da" },
    glow: 0.45,
  },
  {
    sky: { top: "#86a9d6", horizon: "#f6c8a0" },
    fog: { color: "#f0d6c0", density: 0.0026 },
    hemi: { intensity: 0.98, sky: "#efe3ee", ground: "#b8a392", dir: [0.1, 1, 0.05] },
    sun: { dir: [0.895, -0.438, -0.078], intensity: 1.0, color: "#ffcb94" },
    glow: 0.75,
  },
  {
    sky: { top: "#8fc0d8", horizon: "#f1eedc" },
    fog: { color: "#e8e6d3", density: 0.003 },
    hemi: { intensity: 0.84, sky: "#e4ecef", ground: "#aca48e", dir: [0.1, 1, 0.05] },
    sun: { dir: [0.384, -0.743, -0.548], intensity: 1.0, color: "#fff4d6" },
    glow: 0.45,
  },
];

function dress(ctx: DressContext): Owned[] {
  const out: Owned[] = [];
  STAGES.forEach((st, k) => {
    const [cx, cz] = CENTERS[k]!;
    out.push(onStage(skyDome(ctx.scene, ctx.glow, st.sky.top, st.sky.horizon!), k));
    for (const c of inkedClouds(ctx.scene, ctx.glow, ctx.ink, { seed: 1875 + k * 31, cx, cz, count: 13 })) out.push(onStage(c, k));
  });
  // The Trail's afternoon: a thunderhead building over the swamp to the north-east.
  const [tx, tz] = CENTERS[2]!;
  out.push(onStage(thunderhead(ctx.scene, ctx.glow, ctx.ink, { x: tx + 250, z: tz - 170, base: 38, top: 205, width: 150, seed: 441 }), 2));
  out.push(...paintPanels(ctx));
  return out;
}

/**
 * Orlando, drawn after the Florida tourist trade's own graphics: linen postcards, roadside
 * billboards and citrus crate labels. Flat, sun-bleached colour under one ink; three stages, each
 * with its own hour of the day.
 */
export const ORLANDO: MapLook = {
  ...STAGES[0]!,
  shadowDarkness: 0.5,
  ink: { color: "#1f2226", width: 4 },
  stages: STAGES,
  dress,
  // Over the lake at Lake Eola: the fountain, the band shell and the towers of downtown beyond.
  showcase: { target: [-190, 6, 4], radius: 150, alpha: 0.35, beta: 1.2, fov: 0.8, sweep: 0.25, stage: 0 },
};
